import {
  Alert,
  Link,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material"; // ⬅️ added Dialog imports
import checkIcon from "../../../../img/check-icon-green.png";
import { useSearchParams } from "react-router-dom";
import React, { useEffect, useState } from "react";
import dataService from "@/app/services/dataService";
import { useGlobalNotification } from "../../../components/header/GlobalNotificationContext";
import PageLoading from "@/app/components/shared/pageLoading";
import DeliveryNotificationsPanel, {
  DeliveryNotificationsValues,
} from "./deliveryNotificationsPanel";
import {
  GAOrderItem,
  OrderNotificationEnrollmentModel,
  UserSubscribedModel,
} from "@/app/models/checkout/types";

import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { sendGAEvent, setGAContext } from "@/app/utils/googleAnalytics/ga4";
import { OrderNotificationPreferences } from "./orderNotificationPreferences";

interface OrderCompletedProps {
  propOrderNumber?: string;
}

const OrderCompleted: React.FC<OrderCompletedProps> = ({ propOrderNumber }) => {
  const { setNotification } = useGlobalNotification();

  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderIds");
  const isAccountOnHoldDueInactivity =
    searchParams.get("isAccountOnHoldDueInactivity") == "true";
  const isBCPOrderIntegrationOn =
    searchParams.get("isBCPOrderIntegrationOn") == "true";
  const [hasOrderNotificationRole, setHasOrderNotificationRole] =
    useState<boolean>(false);
  const [hasOrderOrderingRole, setHasOrderOrderingRole] =
    useState<boolean>(false);
  const [isSecondaryUser, setIsSecondaryUser] = useState<boolean>(false);
  const [isDeliveryOrder, setIsDeliveryOrder] = useState<boolean>(false);
  const [
    orderNotificationEnrollmentModel,
    setOrderNotificationEnrollmentModel,
  ] = useState<OrderNotificationEnrollmentModel | null>(null);
  const [userSubscribedModel, setUserSubscribedModel] = useState<
    UserSubscribedModel[]
  >([]);
  const [subscribedExtraEmails, setSubscribedExtraEmails] = useState<string[]>(
    []
  );
  const [subscribedExtraPhones, setSubscribedExtraPhones] = useState<string[]>(
    []
  );

  const [orderNumber, setOrderNumber] = useState<string | undefined>(
    propOrderNumber
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const userId = useSelector((state: RootState) => state.userState.user.id);
  const originalUserId = useSelector(
    (state: RootState) => state.userState.user.originalUserId
  );

  const [open, setOpen] = useState(false);
  const [dialogText, setDialogText] = useState<{
    title: string;
    message: string;
  }>({
    title: "",
    message: "",
  });

  const handleSubmit = async (values: DeliveryNotificationsValues) => {
    try {
      const payload = {
        orderIds: [Number(orderId)],
        emails: values.emails,
        phones: values.phones,
      };

      const response =
        await dataService.AddAdditionalOrderDeliverySubscriptions(payload); // boolean in response.data

      if (response?.data === true) {
        const cleanEmails = Array.from(
          new Set(values.emails.map((e) => e.trim()).filter(Boolean))
        );
        const cleanPhones = Array.from(
          new Set(values.phones.map((p) => p.trim()).filter(Boolean))
        );

        setSubscribedExtraEmails(cleanEmails);
        setSubscribedExtraPhones(cleanPhones);
        setDialogText({
          title: "SUCCESS",
          message:
            "Successfully saved additional delivery notification subscriptions.",
        });
      } else {
        setDialogText({
          title: "FAILED",
          message:
            "We couldn't save your notification preferences. Please try again.",
        });
      }
      setOpen(true);
    } catch (error: any) {
      setDialogText({
        title: "ERROR",
        message: error?.message ?? "Something went wrong while saving.",
      });
      setOpen(true);
    }
  };

  useEffect(() => {
    if (!propOrderNumber && orderId) {
      (async () => {
        try {
          setLoading(true);
          await fetchOrderDetails();
        } catch (e) {
          setLoading(false);
        }
      })();
    }
  }, [propOrderNumber, orderId]);

  const fetchOrderDetails = async () => {
    if (!orderId) return;

    const res = await dataService.getOrderCompleted(orderId);

    setHasOrderNotificationRole(res.data.hasOrderNotificationRole);
    setHasOrderOrderingRole(res.data.hasOrderOrderingRole);
    setIsSecondaryUser(res.data.isSecondaryUser);
    var customOrderNumber =
      res.data.checkoutCompletedModelList[0].customOrderNumber ?? "";
    setOrderNumber(customOrderNumber);
    setIsDeliveryOrder(res.data.isDeliveryOrder);
    setOrderNotificationEnrollmentModel(
      res.data.orderNotificationEnrollmentModel ?? null
    );
    setUserSubscribedModel(res.data.usersSubscribedToOrders ?? "");
    setLoading(false);

    var settings = (await dataService.getGASettings()).data;

    setGAContext({
      user_id: originalUserId ?? "",
      algoliaUserToken: settings.algoliaUserToken + "_" + userId,
      algoliaObjectIds: res.data.gaOrderDto.algoliaObjectIds,
      algoliaIndexName: process.env.REACT_APP_ALGOLIA_PRODUCT_INDEX,
    });

    sendGAEvent("purchase", {
      algoliaUserToken: settings.algoliaUserToken,
      algoliaObjectIds: res.data.gaOrderDto.algoliaObjectIds,
      algoliaIndexName: process.env.REACT_APP_ALGOLIA_PRODUCT_INDEX,
      transaction_id: customOrderNumber,
      affiliation: settings.storeName ?? "LansingNOW",
      value: res.data.gaOrderDto.orderTotal,
      currency: "USD",
      tax: 0,
      shipping: 0,
      items: res.data.gaOrderDto.items.map((x: GAOrderItem, index: number) => ({
        item_id: x.id.toString(),
        item_name: x.productName,
        item_brand: x.itemBrand,
        item_variant: x.productName ?? "",
        price: x.price,
        quantity: x.quantity,
        item_category: x.itemCategory,
        affiliation: settings.storeName ?? "LansingNOW",
        index: index.toString(),
      })),
    });
  };

  const handleContinueShopping = () => {
    window.location.href = "/lbpcustomer";
  };

  if (loading) {
    return <PageLoading />;
  }

  return (
    <>
      {isBCPOrderIntegrationOn ? (
        <>
          <div id="ph-title" className="page-title">
            <Typography variant="h1" textAlign={"center"}>
              {" "}
              Thank you for your order!
            </Typography>
          </div>
          <Typography className="checkout-strong-title" sx={{ mb: 4 }}>
            Your order has been submitted. Our servers are currently down but
            once they are back up you will receive an order confirmation.
          </Typography>
        </>
      ) : (
        <>
          <div id="ph-title" className="page-title">
            <Typography variant="h1" textAlign={"center"}>
              Order Confirmation
            </Typography>
          </div>
          <Box
            sx={{
              mx: "auto",
              px: 2,
              mb: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{ fontWeight: 600 }}
              variant="h4"
              gutterBottom
              textAlign={"center"}
            >
              Thank you for your order!
            </Typography>

            <Box display="flex" justifyContent="center" my={3}>
              <img
                src={checkIcon}
                alt="Order Confirmed"
                style={{ width: 150, height: 150 }}
              />
            </Box>
            {!isAccountOnHoldDueInactivity && orderNumber && (
              <Typography variant="body1" sx={{ mb: 1 }} align="center">
                Your order number is{" "}
                <Link
                  href={`${process.env
                    .REACT_APP_WEB_URL!}/orderdetails/${orderId}`}
                  underline="hover"
                  color="primary"
                  sx={{ fontWeight: 700, color: "#54afe2" }}
                >
                  #{orderNumber}
                </Link>
              </Typography>
            )}
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign={"center"}
            >
              You will receive an order confirmation shortly.
            </Typography>

            {error && (
              <Box mt={2}>
                <Alert severity="error">{error}</Alert>
              </Box>
            )}

            {(!isSecondaryUser ||
              (isSecondaryUser && hasOrderNotificationRole)) &&
            isDeliveryOrder ? (
              <>
                {!orderNotificationEnrollmentModel?.isEnrolled && (
                  <OrderNotificationPreferences />
                )}

                {orderNotificationEnrollmentModel?.isEnrolled &&
                  (orderNotificationEnrollmentModel?.enrollmentNotificationTypes?.some(
                    (t) =>
                      t.notificationType === "DeliveryStatus" && t.isEnrolled
                  ) ??
                    false) && (
                    <div style={{ maxWidth: 800, margin: "40px auto" }}>
                      <DeliveryNotificationsPanel
                        initialContactByEmailCC={true}
                        userSubscribedModel={userSubscribedModel}
                        subscribedExtraEmails={subscribedExtraEmails}
                        subscribedExtraPhones={subscribedExtraPhones}
                        onSubmit={handleSubmit}
                        onContinueShopping={handleContinueShopping}
                      />
                    </div>
                  )}
              </>
            ) : (
              hasOrderOrderingRole && (
                <div style={{ textAlign: "center", marginTop: 40 }}>
                  <Button
                    className="blueButtonOutline"
                    size="large"
                    onClick={handleContinueShopping}
                    disabled={!handleContinueShopping}
                  >
                    CONTINUE SHOPPING
                  </Button>
                </div>
              )
            )}
          </Box>
        </>
      )}

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle sx={{ textAlign: "center", fontWeight: "bold" }}>
          {dialogText.title}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">{dialogText.message}</Typography>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "center",
            pb: 2,
          }}
        >
          <Button
            className="whiteButtonOutline"
            sx={{
              fontSize: "17px",
              height: "50px",
              px: "35px !important",
            }}
            onClick={() => setOpen(false)}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OrderCompleted;
